import React, { useState, useEffect, useRef } from 'react';
import { brandLogo, BRAND_LOGO_ALT } from '../branding';
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faHome, faUser, faCalendarDay,
    faChartPie, faGear, faUsers, faChevronDown, faChevronUp,
    faCode, faChalkboardTeacher, faBuilding, faUserTie, faFileInvoice,
    faMoneyBill, faFileAlt, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase';
import { recordAdminActivity } from '../utils/adminPresence';
import { normalizeAdminType, resolveAdminRecord } from '../utils/adminAccess';

const LEFTBAR_COLLAPSE_KEY = 'leftbarCollapseState';
const LEFTBAR_SCROLL_KEY = 'leftbarScrollTop';

const readStoredCollapseState = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const rawState = window.localStorage.getItem(LEFTBAR_COLLAPSE_KEY);
        return rawState ? JSON.parse(rawState) : null;
    } catch (error) {
        console.error('Failed to read left bar state:', error);
        return null;
    }
};

const readStoredScrollTop = () => {
    if (typeof window === 'undefined') {
        return 0;
    }

    const rawValue = window.sessionStorage.getItem(LEFTBAR_SCROLL_KEY);
    const parsedValue = Number(rawValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const routeLabels = {
    "/dashboard": "Opened Dashboard",
    "/applications": "Opened Applications",
    "/active-students": "Opened Active Students",
    "/learning-breaks": "Opened Learning Breaks",
    "/graduated-students": "Opened Graduated Students",
    "/students-graduating": "Opened Students Graduating",
    "/never-joined": "Opened Never Joined",
    "/other-students": "Opened Other Students",
    "/sources-of-students": "Opened Sources of Students",
    "/conversion-by-source": "Opened Conversion by Source",
    "/trends-over-time": "Opened Trends Over Time",
    "/course-by-source": "Opened Course by Source",
    "/demographics-by-source": "Opened Demographics by Source",
    "/campus-type": "Opened Campus Type by Source",
    "/drop-off-analysis": "Opened Drop-off Analysis",
    "/application-to-adm-time": "Opened Application to Admission Time",
    "/session-time-demand": "Opened Session Time Demand",
    "/distribution-by-country": "Opened Distribution By Country",
    "/country-analysis": "Opened Nationality vs Course Choice",
    "/courses": "Opened Courses",
    "/intakes": "Opened Intakes",
    "/session-settings": "Opened Session Settings",
    "/darasa": "Opened Classes",
    "/instructors": "Opened Instructors",
    "/branches": "Opened Campuses",
    "/income": "Opened School Fee Income",
    "/admission-fees": "Opened Admission Fees",
    "/other-income": "Opened Other Income",
    "/sent-invoices": "Opened Invoices",
    "/projected-income": "Opened Projected Income",
    "/fee-balances": "Opened Fee Balances",
    "/vendors": "Opened Vendors",
    "/fixed-bills": "Opened Fixed Bills",
    "/pay-bills": "Opened Pay Bills",
    "/expenses": "Opened Operations Spend",
    "/requisition-approvals": "Opened Requisition Approvals",
    "/debts-loans": "Opened Debts / Loans",
    "/finance-reports": "Opened Finance Reports",
    "/employees": "Opened Employees",
    "/leave-request": "Opened Leave Request",
    "/pay-salaries": "Opened Pay Salaries",
    "/directors": "Opened Directors",
    "/shareholders": "Opened Shareholders",
    "/hr-reports": "Opened HR Reports",
    "/assets": "Opened Assets",
    "/fee-reminders": "Opened Fee Reminders",
    "/admins": "Opened Admin Management",
    "/activity-logs": "Opened Activity Logs",
    "/profile": "Opened Profile",
    "/settings": "Opened Settings"
};

const getRouteLabel = (pathname) => {
    if (routeLabels[pathname]) return routeLabels[pathname];
    return null;
};

function LeftBar() {
    let navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();
    const lastLoggedRef = useRef('');
    const leftNavRef = useRef(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [adminRole, setAdminRole] = useState('admin');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user || null);
            if (!user) {
                navigate("/login");
            }
        });

        return () => unsubscribe();
    }, [auth, navigate]);

    useEffect(() => {
        let cancelled = false;

        const fetchAdminRole = async () => {
            if (!currentUser) {
                setAdminRole('admin');
                return;
            }

            try {
                const adminRecord = await resolveAdminRecord(currentUser);
                if (!cancelled) {
                    setAdminRole(normalizeAdminType(adminRecord?.adminType));
                }
            } catch (error) {
                console.error('Failed to load admin role for left bar:', error);
                if (!cancelled) {
                    setAdminRole('admin');
                }
            }
        };

        fetchAdminRole();

        return () => {
            cancelled = true;
        };
    }, [currentUser]);

    const storedCollapseState = readStoredCollapseState();
    const [isCollapsed1, setIsCollapsed1] = useState(storedCollapseState?.isCollapsed1 ?? true);
    const [isCollapsed2, setIsCollapsed2] = useState(storedCollapseState?.isCollapsed2 ?? true);
    const [isCollapsed3, setIsCollapsed3] = useState(storedCollapseState?.isCollapsed3 ?? true);
    const [isCollapsed5, setIsCollapsed5] = useState(storedCollapseState?.isCollapsed5 ?? true);

    const toggleCollapse1 = () => setIsCollapsed1(!isCollapsed1);
    const toggleCollapse2 = () => setIsCollapsed2(!isCollapsed2);
    const toggleCollapse3 = () => setIsCollapsed3(!isCollapsed3);
    const toggleCollapse5 = () => setIsCollapsed5(!isCollapsed5);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        window.localStorage.setItem(LEFTBAR_COLLAPSE_KEY, JSON.stringify({
            isCollapsed1,
            isCollapsed2,
            isCollapsed3,
            isCollapsed5
        }));
    }, [isCollapsed1, isCollapsed2, isCollapsed3, isCollapsed5]);

    useEffect(() => {
        if (!leftNavRef.current) return;
        leftNavRef.current.scrollTop = readStoredScrollTop();
    }, []);

    const handleLeftNavScroll = () => {
        if (typeof window === 'undefined' || !leftNavRef.current) return;
        window.sessionStorage.setItem(LEFTBAR_SCROLL_KEY, String(leftNavRef.current.scrollTop));
    };

    useEffect(() => {
        if (!currentUser) return;
        const label = getRouteLabel(location.pathname);
        if (!label) return;
        if (lastLoggedRef.current === location.pathname) return;
        lastLoggedRef.current = location.pathname;

        const adminName = currentUser.displayName || currentUser.email || 'Admin';
        addDoc(collection(db, 'AdminActivity'), {
            adminId: currentUser.uid,
            adminName,
            action: 'Opened Page',
            details: label,
            path: location.pathname,
            createdAt: serverTimestamp()
        }).catch((error) => {
            console.error('Failed to log page open:', error);
        });
    }, [currentUser, location.pathname]);

    useEffect(() => {
        if (!currentUser) return;

        recordAdminActivity({
            userId: currentUser.uid,
            email: currentUser.email,
            path: location.pathname,
            label: getRouteLabel(location.pathname) || 'Active on platform',
            source: 'route',
            force: true
        }).catch((error) => {
            console.error('Failed to update admin last active:', error);
        });
    }, [currentUser, location.pathname]);

    useEffect(() => {
        const syncActivity = (source) => {
            if (!currentUser) return;

            recordAdminActivity({
                userId: currentUser.uid,
                email: currentUser.email,
                path: location.pathname,
                label: getRouteLabel(location.pathname) || 'Active on platform',
                source
            }).catch((error) => {
                console.error('Failed to refresh admin activity:', error);
            });
        };

        const handleFocus = () => syncActivity('focus');
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncActivity('visibility');
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [currentUser, location.pathname]);

    const isSuperAdmin = adminRole === 'superAdmin';

    return (
        <div className='leftNav' ref={leftNavRef} onScroll={handleLeftNavScroll}>
            <div className='logoContainer'>
                <img src={brandLogo} alt={BRAND_LOGO_ALT} className='dashnavLogo' />
            </div>
            <br />
            <p>HOME</p>
            <NavLink to="/dashboard" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faHome} className="laftNavIcon activelaftNavIcon" />
                Dashboard
            </NavLink>
            <NavLink to="/applications" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faFileInvoice} className="laftNavIcon" />
                Applications
            </NavLink>
            <NavLink to="/fee-reminders" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faCalendarDay} className="laftNavIcon " />
                Fee Reminders
            </NavLink>
            {isSuperAdmin && (
                <div className='nav-item '>
                    <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse5} style={{ cursor: 'pointer' }}>
                        <div>
                            <FontAwesomeIcon icon={faChartPie} className="laftNavIcon" />
                            <span>Data Analytics</span>
                        </div>
                        <FontAwesomeIcon icon={isCollapsed5 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                    </div>
                    {!isCollapsed5 && (
                        <div className='submenu'>
                            <NavLink to="/sources-of-students" className='nav-link'>Sources of Students</NavLink>
                            <NavLink to="/conversion-by-source" className='nav-link'>Conversion by Source</NavLink>
                            <NavLink to="/trends-over-time" className='nav-link'>Trends Over Time</NavLink>
                            <NavLink to="/course-by-source" className='nav-link'>Course by Source</NavLink>
                            <NavLink to="/demographics-by-source" className='nav-link'>Demographics by Source</NavLink>
                            <NavLink to="/campus-type" className='nav-link'>Campus Type by Source</NavLink>
                            <NavLink to="/drop-off-analysis" className='nav-link'>Drop-off Analysis</NavLink>
                            <NavLink to="/application-to-adm-time" className='nav-link'>Application to Admission Time</NavLink>
                            <NavLink to="/session-time-demand" className='nav-link'>Session Time Demand</NavLink>
                            <NavLink to="/distribution-by-country" className='nav-link'>Distribution By Country</NavLink>
                            <NavLink to="/country-analysis" className='nav-link'>Nationality vs Course Choice</NavLink>
                        </div>
                    )}
                </div>
            )}

            {/* Students */}
            <div className='nav-item '>
                <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse1} style={{ cursor: 'pointer' }}>
                    <div>
                        <FontAwesomeIcon icon={faUsers} className="laftNavIcon" />
                        <span>Students</span>
                    </div>
                    <FontAwesomeIcon icon={isCollapsed1 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                </div>
                {!isCollapsed1 && (
                    <div className='submenu'>
                        <NavLink to="/active-students" className='nav-link'>Active Students</NavLink>
                        <NavLink to="/learning-breaks" className='nav-link'>Learning Breaks</NavLink>
                        <NavLink to="/graduated-students" className='nav-link'>Graduated Students</NavLink>
                        <NavLink to="/students-graduating" className='nav-link'>Students Graduating</NavLink>
                        <NavLink to="/never-joined" className='nav-link'>Never Joined</NavLink>
                        <NavLink to="/other-students" className='nav-link'>Other Students</NavLink>
                    </div>
                )}
            </div>

            <p>FINANCE & HR</p>
            <div className='nav-item '>
                <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse2} style={{ cursor: 'pointer' }}>
                    <div>
                        <FontAwesomeIcon icon={faMoneyBill} className="laftNavIcon" />
                        <span>Finance</span>
                    </div>
                    <FontAwesomeIcon icon={isCollapsed2 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                </div>
                {!isCollapsed2 && (
                    <div className='submenu'>
                        <NavLink to="/income" className='nav-link'>School fee Income</NavLink>
                        <NavLink to="/admission-fees" className='nav-link'>Admission Fees</NavLink>
                        <NavLink to="/sent-invoices" className='nav-link'>Invoices</NavLink>
                        {isSuperAdmin && (
                            <>
                                <NavLink to="/other-income" className='nav-link'>Other Income</NavLink>
                                <NavLink to="/projected-income" className='nav-link'>Projected Income</NavLink>
                                <NavLink to="/fee-balances" className='nav-link'>Fee Balances</NavLink>
                                <NavLink to="/vendors" className='nav-link'>Vendors</NavLink>
                                <NavLink to="/fixed-bills" className='nav-link'>Fixed Bills</NavLink>
                                <NavLink to="/expenses" className='nav-link'>Operations Spend</NavLink>
                                <NavLink to="/requisition-approvals" className='nav-link'>Requisition Approvals</NavLink>
                                <NavLink to="/debts-loans" className='nav-link'>Debts / Loans</NavLink>
                                <NavLink to="/finance-reports" className='nav-link'>Reports</NavLink>
                            </>
                        )}
                    </div>
                )}
            </div>
            {isSuperAdmin && (
                <>
                    <div className='nav-item '>
                        <div className='d-flex align-items-center leftNavLinks leftLinkCont dropdownMyCont' onClick={toggleCollapse3} style={{ cursor: 'pointer' }}>
                            <div>
                                <FontAwesomeIcon icon={faUserTie} className="laftNavIcon" />
                                <span>HR</span>
                            </div>
                            <FontAwesomeIcon icon={isCollapsed3 ? faChevronDown : faChevronUp} className="ml-auto leftLinkNotif2" />
                        </div>
                        {!isCollapsed3 && (
                            <div className='submenu'>
                                <NavLink to="/employees" className='nav-link'>Employees</NavLink>
                                <NavLink to="/leave-request" className='nav-link'>Leave Request</NavLink>
                                <NavLink to="/pay-salaries" className='nav-link'>Pay Salaries</NavLink>
                                <NavLink to="/directors" className='nav-link'>Directors</NavLink>
                                <NavLink to="/shareholders" className='nav-link'>Shareholders</NavLink>
                                <NavLink to="/hr-reports" className='nav-link'>Reports</NavLink>
                            </div>
                        )}
                    </div>
                    <NavLink to="/admins" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faUserTie} className="laftNavIcon " />
                        Admins
                    </NavLink>
                    <NavLink to="/assets" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faFileAlt} className="laftNavIcon" />
                        Assets
                    </NavLink>
                </>
            )}

            <p>ACADEMIC</p>
            {isSuperAdmin && (
                <NavLink to="/courses" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                    <FontAwesomeIcon icon={faCode} className="laftNavIcon" />
                    Courses
                </NavLink>
            )}
            <NavLink to="/session-settings" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faCalendarDay} className="laftNavIcon" />
                Session Settings
            </NavLink>
            <NavLink to="/intakes" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faCalendarDay} className="laftNavIcon" />
                Intakes
            </NavLink>
            <NavLink to="/darasa" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faChalkboardTeacher} className="laftNavIcon" />
                Classes
            </NavLink>
            {isSuperAdmin && (
                <>
                    <NavLink to="/instructors" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faUserTie} className="laftNavIcon" />
                        Instructors
                    </NavLink>
                    <NavLink to="/branches" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                        <FontAwesomeIcon icon={faBuilding} className="laftNavIcon" />
                        Campuses
                    </NavLink>
                </>
            )}

            <p>ACCOUNT</p>
            <NavLink to="/profile" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                <FontAwesomeIcon icon={faUser} className="laftNavIcon " />
                Profile
            </NavLink>
            {isSuperAdmin && (
                <NavLink to="/activity-logs" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                    <FontAwesomeIcon icon={faClockRotateLeft} className="laftNavIcon " />
                    Activity Logs
                </NavLink>
            )}
            {isSuperAdmin && (
                <NavLink to="/settings" className='leftNavLinks leftLinkCont d-flex align-items-center'>
                    <FontAwesomeIcon icon={faGear} className="laftNavIcon " />
                    Settings
                </NavLink>
            )}
        </div>
    );
}

export default LeftBar;
